<template>
  <div class="main-layout">
    <Sidebar />
    
    <main class="layout-main">
        <header class="layout-header">
          <div class="header-content">
            <!-- 移动端汉堡菜单按钮 -->
            <div class="mobile-menu-btn" @click="sidebarStore.toggleMobileDrawer">
              <el-icon :size="22"><Expand /></el-icon>
            </div>

            <div class="header-center">
              <el-breadcrumb separator="/">
                <el-breadcrumb-item v-for="(item, index) in breadcrumbItems" :key="`breadcrumb-${index}-${item.path}`" :to="item.path">
                  <el-icon v-if="item.isHome"><HomeFilled /></el-icon>
                  <span>{{ item.title }}</span>
                </el-breadcrumb-item>
              </el-breadcrumb>
            </div>
    
            <div class="header-right">
            <!-- ADP HeaderBar 标配：刷新当前页按钮 -->
            <el-tooltip content="刷新当前页面" placement="bottom" :show-after="300">
              <div class="header-icon-btn" @click="handleHeaderRefresh">
                <el-icon :size="18" :class="{ 'is-refreshing': headerRefreshing }"><Refresh /></el-icon>
              </div>
            </el-tooltip>
            <div class="search-wrapper">
              <el-popover
                placement="bottom-start"
                :width="commandPaletteWidth"
                trigger="click"
                v-model:visible="commandPaletteVisible"
                :popper-class="'command-palette-popover'"
                :show-arrow="false"
                :offset="8"
              >
                <template #reference>
                  <div class="search-trigger">
                    <el-icon><Search /></el-icon>
                    <span class="search-text">全局搜索</span>
                    <span class="search-shortcut">Ctrl+K</span>
                  </div>
                </template>
                
                <div class="command-palette">
                  <!-- 搜索输入 -->
                  <div class="command-input-wrapper">
                    <el-icon class="input-icon"><Search /></el-icon>
                    <input
                      ref="commandInputRef"
                      v-model="commandKeyword"
                      class="command-input"
                      placeholder="输入命令、导航或搜索内容..."
                      @keydown="handleCommandKeydown"
                      @input="handleCommandInput"
                    />
                    <kbd class="escape-hint">ESC</kbd>
                  </div>
                  
                  <!-- 命令分类结果 -->
                  <div class="command-results" v-if="commandKeyword || recentCommands.length > 0">
                    <!-- 导航命令 -->
                    <div class="command-group" v-if="navigationCommands.length > 0">
                      <div class="command-group-header">
                        <el-icon><Location /></el-icon>
                        <span>导航</span>
                      </div>
                      <div class="command-list">
                        <div 
                          v-for="(cmd, index) in navigationCommands" 
                          :key="cmd.id"
                          class="command-item"
                          :class="{ 'is-active': index === activeCommandIndex && currentGroup === 'navigation' }"
                          @click="executeCommand(cmd)"
                          @mouseenter="setActiveCommand(index, 'navigation')"
                        >
                          <div class="command-icon" :style="{ backgroundColor: cmd.iconBg }">
                            <el-icon><component :is="cmd.icon" /></el-icon>
                          </div>
                          <div class="command-content">
                            <div class="command-title">{{ cmd.title }}</div>
                            <div class="command-desc">{{ cmd.path }}</div>
                          </div>
                          <el-tag v-if="cmd.shortcut" size="small" class="command-shortcut">{{ cmd.shortcut }}</el-tag>
                        </div>
                      </div>
                    </div>
                    
                    <!-- 快捷操作 -->
                    <div class="command-group" v-if="actionCommands.length > 0">
                      <div class="command-group-header">
                        <el-icon><Lightning /></el-icon>
                        <span>快捷操作</span>
                      </div>
                      <div class="command-list">
                        <div 
                          v-for="(cmd, index) in actionCommands" 
                          :key="cmd.id"
                          class="command-item"
                          :class="{ 'is-active': index === activeCommandIndex && currentGroup === 'actions' }"
                          @click="executeCommand(cmd)"
                          @mouseenter="setActiveCommand(index, 'actions')"
                        >
                          <div class="command-icon" :style="{ backgroundColor: cmd.iconBg }">
                            <el-icon><component :is="cmd.icon" /></el-icon>
                          </div>
                          <div class="command-content">
                            <div class="command-title">{{ cmd.title }}</div>
                            <div class="command-desc">{{ cmd.description }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- 模具搜索结果 -->
                    <div class="command-group" v-if="moldSearchResults.length > 0">
                      <div class="command-group-header">
                        <el-icon><Box /></el-icon>
                        <span>模具</span>
                        <span class="group-count">{{ moldSearchResults.length }}</span>
                      </div>
                      <div class="command-list">
                        <div 
                          v-for="(mold, index) in moldSearchResults" 
                          :key="'mold-' + mold.id"
                          class="command-item"
                          :class="{ 'is-active': index === activeCommandIndex && currentGroup === 'molds' }"
                          @click="goToMold(mold)"
                          @mouseenter="setActiveCommand(index, 'molds')"
                        >
                          <div class="command-icon mold-icon">
                            <el-icon><Box /></el-icon>
                          </div>
                          <div class="command-content">
                            <div class="command-title" v-html="highlightKeyword(mold.moldCode || '', commandKeyword)"></div>
                            <div class="command-desc">
                              <span v-if="mold.productSpec" v-html="highlightKeyword(mold.productSpec, commandKeyword)"></span>
                              <span v-if="mold.processingLeader" class="leader">· {{ mold.processingLeader }}</span>
                            </div>
                          </div>
                          <el-tag size="small" :type="getStatusType(mold.status)">{{ mold.status }}</el-tag>
                        </div>
                      </div>
                    </div>
                    
                    <!-- 无结果 -->
                    <div class="command-empty" v-if="totalResults === 0 && !commandLoading">
                      <el-icon><Search /></el-icon>
                      <span>未找到相关结果</span>
                      <div class="empty-tips">
                        尝试搜索：模具编号、页面名称、快捷操作
                      </div>
                    </div>
                  </div>
                  
                  <!-- 搜索历史和提示 -->
                  <div class="command-default" v-else>
                    <!-- 快捷键提示 -->
                    <div class="keyboard-hints">
                      <div class="hint-item">
                        <kbd>↑</kbd><kbd>↓</kbd>
                        <span>选择</span>
                      </div>
                      <div class="hint-item">
                        <kbd>Enter</kbd>
                        <span>执行</span>
                      </div>
                      <div class="hint-item">
                        <kbd>Tab</kbd>
                        <span>分类切换</span>
                      </div>
                    </div>
                    
                    <!-- 快捷命令 -->
                    <div class="quick-commands">
                      <div class="quick-command-item" @click="goToPath('/mold/create')">
                        <el-icon><Plus /></el-icon>
                        <span>新增模具</span>
                      </div>
                      <div class="quick-command-item" @click="goToPath('/processing/task-board')">
                        <el-icon><Grid /></el-icon>
                        <span>加工看板</span>
                      </div>
                      <div class="quick-command-item" @click="goToPath('/mold/storage')">
                        <el-icon><Box /></el-icon>
                        <span>入库管理</span>
                      </div>
                      <div class="quick-command-item" @click="goToPath('/mold/borrow')">
                        <el-icon><Switch /></el-icon>
                        <span>领用申请</span>
                      </div>
                      <div class="quick-command-item" @click="goToPath('/system/settings')">
                        <el-icon><Setting /></el-icon>
                        <span>系统设置</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 加载状态 -->
                  <div class="command-loading" v-if="commandLoading">
                    <el-icon class="is-loading"><Loading /></el-icon>
                    <span>搜索中...</span>
                  </div>
                </div>
              </el-popover>
            </div>

            <el-popover
              placement="bottom-end"
              :width="360"
              :offset="8"
              trigger="click"
              v-model:visible="notificationVisible"
              :popper-class="'notification-popover'"
            >
              <template #reference>
                <div class="notification-trigger">
                  <el-badge :value="unreadCount" :hidden="unreadCount === 0" :max="99">
                    <el-icon :size="20"><Bell /></el-icon>
                  </el-badge>
                </div>
              </template>
              
              <div class="notification-panel">
                <div class="notification-header">
                  <span class="notification-title">消息通知</span>
                  <span class="mark-all-read" v-if="unreadCount > 0" @click="markAllAsRead">全部已读</span>
                </div>
                
                <div class="notification-tabs">
                  <span 
                    class="tab-item" 
                    :class="{ active: notificationTab === 'all' }"
                    @click="notificationTab = 'all'"
                  >全部</span>
                  <span 
                    class="tab-item" 
                    :class="{ active: notificationTab === 'unread' }"
                    @click="notificationTab = 'unread'"
                  >未读 ({{ unreadCount }})</span>
                </div>
                
                <div class="notification-list" v-if="filteredNotifications.length > 0">
                  <div 
                    v-for="notification in filteredNotifications" 
                    :key="notification.id"
                    class="notification-item"
                    :class="{ unread: !notification.read }"
                    @click="handleNotificationClick(notification)"
                  >
                    <div class="notification-icon" :style="{ backgroundColor: getNotificationIconBgColor(notification.notificationType || 'SYSTEM'), color: getNotificationIconColor(notification.notificationType || 'SYSTEM') }">
                      <el-icon>
                        <component :is="getNotificationIconComponent(notification.notificationType || 'SYSTEM')" />
                      </el-icon>
                    </div>
                    <div class="notification-content">
                      <div class="notification-text">{{ notification.title }}</div>
                      <div class="notification-desc">{{ notification.content }}</div>
                      <div class="notification-time">{{ notification.time }}</div>
                    </div>
                    <div class="notification-dot" v-if="!notification.read"></div>
                  </div>
                </div>
                
                <div class="notification-empty" v-else>
                  <el-icon :size="48"><Bell /></el-icon>
                  <span>暂无消息</span>
                </div>
                
                <div class="notification-footer" v-if="notifications.length > 0">
                  <span @click="goToNotificationCenter">查看全部消息</span>
                </div>
              </div>
            </el-popover>

            <el-dropdown @command="handleCommand">
              <div class="user-dropdown">
                <el-avatar :size="36" :src="currentUser?.avatar" :icon="UserFilled" />
                <span class="username">{{ currentUsername }}</span>
                <el-icon class="dropdown-icon"><ArrowDown /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="logout">
                    <el-icon><SwitchButton /></el-icon>
                    退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>

        <!-- 多标签页栏：融入顶栏同一固定块（第二行），跟随顶栏一起固定 -->
        <TabBar />
      </header>

      <div class="layout-content">
        <router-view v-slot="{ Component, route: viewRoute }">
          <keep-alive :include="keepAliveNames">
            <component :is="Component" :key="viewRoute.path" />
          </keep-alive>
        </router-view>
      </div>
  
      <footer class="layout-footer">
        <div class="footer-content">
          <span class="footer-text">
            © {{ currentYear }} 安天模具管理系统 · All rights reserved.
          </span>
          <span class="footer-divider">·</span>
          <a class="footer-beian-link" href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">赣ICP备2026018388号-1</a>
          <span class="footer-divider">·</span>
          <a class="footer-gongan-link" href="https://beian.mps.gov.cn/#/query/webSearch?code=36049002000257" target="_blank" rel="noopener noreferrer">
            <img src="/gongan-badge.png" alt="公安备案" class="footer-gongan-icon" />
            赣公网安备36049002000257号
          </a>
          <VersionDisplay />
        </div>
      </footer>
      <VersionBadge />
    </main>
  </div>
</template>
  
<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  HomeFilled,
  UserFilled,
  ArrowDown,
  User,
  SwitchButton,
  Search,
  FolderOpened,
  Document,
  Loading,
  Clock,
  Bell,
  Box,
  Odometer,
  InfoFilled,
  Location,
  Lightning,
  Plus,
  Grid,
  Setting,
  Tools,
  List,
  User as UserIcon,
  DocumentCopy,
  DataLine,
  Monitor,
  Setting as SettingIcon,
  Refresh,
  CircleCheck,
  Warning,
  Back,
  Folder
} from '@element-plus/icons-vue'
import Sidebar from './Sidebar.vue'
import TabBar from './TabBar.vue'
import VersionBadge from '@/components/VersionBadge.vue'
import VersionDisplay from '@/components/common/VersionDisplay.vue'
import { useSidebarStore } from '@/stores/sidebar'
import { useTabsStore } from '@/stores/tabs'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notificationStore'
import { getMoldList } from '@/api/mold'
import type { Mold } from '@/types/mold'
import { menuConfig } from '@/constants/menuConfig'
import logger from '@/utils/logger'
import {
  getNotificationIcon,
  getNotificationColor,
  getNotificationBgColor,
  mapBackendNotificationType
} from '@/constants/notificationConfig'
import type { Component } from 'vue'

interface BreadcrumbItem {
  title: string
  path: string
  isHome?: boolean
}

interface CommandItem {
  id: string
  type: 'navigation' | 'action'
  title: string
  description?: string
  path?: string
  icon: string
  iconBg: string
  shortcut?: string
  action?: () => void
}

interface Notification {
  id: number
  type: 'borrow' | 'storage' | 'check' | 'system' | 'warning' | 'return' | 'task' | 'processing'
  title: string
  content: string
  time: string
  read: boolean
  moldId?: number
  notificationType?: string // 保存原始类型用于图标和颜色映射
}

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const sidebarStore = useSidebarStore()

// 通知类型图标映射（使用共享配置）
const getNotificationIconComponent = (notificationType: string): Component => {
  return getNotificationIcon(notificationType)
}

// 通知类型颜色映射（使用共享配置）
const getNotificationIconColor = (notificationType: string): string => {
  return getNotificationColor(notificationType)
}

// 通知类型背景色映射（使用共享配置）
const getNotificationIconBgColor = (notificationType: string): string => {
  return getNotificationBgColor(notificationType)
}

// ========== Notification Panel ==========
const notificationVisible = ref(false)
const notificationTab = ref<'all' | 'unread'>('all')

// 通知数据（从API获取）
const notifications = ref<Notification[]>([])

// 从后端API加载通知列表
const loadNotifications = async () => {
  try {
    const response = await notificationStore.fetchNotifications({
      unreadOnly: false,
      pageSize: 10
    })

    // 将后端通知数据转换为前端格式
    notifications.value = (response || []).map((item: any) => ({
      id: item.id,
      type: mapBackendNotificationType(item.notificationType),
      title: item.title,
      content: item.content,
      time: item.displayTime,
      read: !item.unread,
      moldId: item.relatedId,
      // 保存原始类型用于图标和颜色
      notificationType: item.notificationType
    }))
  } catch (error) {
    logger.error('加载通知失败:', error)
  }
}

// 未读数量（使用 store）
const unreadCount = computed(() => notificationStore.unreadCount)

// 刷新通知数据
const refreshNotifications = async () => {
  await notificationStore.fetchUnreadCount()

  // 只有当通知面板打开时才加载完整列表
  if (notificationVisible.value) {
    await loadNotifications()
  }
}

const filteredNotifications = computed(() => {
  if (notificationTab.value === 'unread') {
    return notifications.value.filter(n => !n.read)
  }
  return notifications.value
})

const markAllAsRead = async () => {
  try {
    await notificationStore.markAllAsRead()
    // 刷新通知
    await refreshNotifications()
    ElMessage.success('已标记全部为已读')
  } catch (error) {
    ElMessage.error('标记已读失败')
  }
}

const handleNotificationClick = async (notification: Notification) => {
  // 标记为已读
  if (!notification.read) {
    try {
      await notificationStore.markAsRead(notification.id)
      notification.read = true
    } catch (error) {
      logger.error('标记已读失败:', error)
    }
  }

  // 跳转到相关页面
  if (notification.moldId) {
    router.push(`/mold/${notification.moldId}`)
    notificationVisible.value = false
  }
}

const goToNotificationCenter = () => {
  router.push('/system/notification')
  notificationVisible.value = false
}

// 监听通知面板打开状态
watch(notificationVisible, async (visible) => {
  if (visible) {
    // 打开时加载通知列表
    await loadNotifications()
  }
})

const currentUser = computed(() => authStore.user)
const currentUsername = computed(() => currentUser.value?.realName || currentUser.value?.username || '用户')
const currentYear = computed(() => new Date().getFullYear())

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const items: BreadcrumbItem[] = [
    { title: '首页', path: '/home', isHome: true }
  ]
  
  // 获取当前路由路径
  const currentPath = route.path
  
  // 解析路径段（例如 '/mold/123' -> ['mold', '123']）
  const pathParts = currentPath.split('/').filter(p => p)
  const parentSegment = pathParts[0] // 例如 'mold'
  
  // 递归查找包含当前路径段的父级菜单
  const findParentMenu = (menus: any[]): any | null => {
    for (const menu of menus) {
      if (menu.children) {
        // 检查是否有子项的路径以该段开头
        for (const child of menu.children) {
          if (child.path) {
            const childSegment = child.path.split('/').filter(p => p)[0]
            if (childSegment === parentSegment) {
              return menu
            }
          }
        }
        // 递归检查子菜单
        const found = findParentMenu(menu.children)
        if (found) return found
      }
    }
    return null
  }
  
  const parentMenu = findParentMenu(menuConfig)
  
  // 如果找到父级菜单（如'模库管理'）
  if (parentMenu) {
    // 查找父级菜单的默认子项（第一个包含'/list'的子项，或第一个子项）
    const defaultChild = parentMenu.children?.find(c => c.path?.includes('/list')) || parentMenu.children?.[0]
    
    // 添加父级菜单项（路径设为默认子项的路径）
    if (defaultChild) {
      items.push({
        title: parentMenu.label,
        path: defaultChild.path
      })
    }
    
    // 检查是否为动态路由（详情/编辑页面）
    const lastSegment = pathParts[pathParts.length - 1]
    const isDynamicRoute = lastSegment && (
      lastSegment.match(/^\d+$/) || // /mold/123
      currentPath.includes('/edit/') || // /mold/edit/123
      currentPath.includes('/create') // /mold/create
    )
    
    // 对于动态路由，需要添加列表页（如果列表页不是当前路由）
    if (isDynamicRoute && defaultChild && defaultChild.path !== currentPath) {
      // 添加列表页
      items.push({
        title: defaultChild.label,
        path: defaultChild.path
      })
    }
    
    // 如果当前路由不是父级菜单的默认子项，且不是动态路由，则添加当前路由作为子项
    if (!isDynamicRoute && defaultChild && currentPath !== defaultChild.path) {
      // 查找当前路由在父级菜单中的直接子项
      const matchedChild = parentMenu.children?.find(c => c.path === currentPath)
      if (matchedChild) {
        items.push({
          title: matchedChild.label,
          path: currentPath
        })
      }
    }
  }
  
  // 添加当前路由标题（如果不是首页）
  if (route.meta?.title && route.path !== '/home') {
    // 检查是否已经添加过相同标题和路径的项
    const alreadyAdded = items.some(item => item.title === route.meta.title && item.path === route.path)
    if (!alreadyAdded) {
      items.push({
        title: route.meta.title as string,
        path: route.path
      })
    }
  }
  
  return items
})

// Helper function to find menu item by path
const findMenuItemByPath = (menus: any[], path: string): any | null => {
  for (const menu of menus) {
    if (menu.path === path) {
      return menu
    }
    if (menu.children) {
      const found = findMenuItemByPath(menu.children, path)
      if (found) return found
    }
  }
  return null
}

// Helper function to find parent menus (returns array of parent menus from root to immediate parent)
const findParentMenus = (menus: any[], targetMenu: any, parents: any[] = []): any[] => {
  for (const menu of menus) {
    if (menu === targetMenu) {
      return parents
    }
    if (menu.children) {
      const found = findParentMenus(menu.children, targetMenu, [...parents, menu])
      if (found.length > 0 || found === parents) {
        return found
      }
    }
  }
  return parents
}

// Helper function to find menu item by path prefix (for dynamic routes like /mold/:id)
const findMenuItemByPathPrefix = (menus: any[], path: string): any | null => {
  for (const menu of menus) {
    // Check if this menu has children and any child's path is a prefix of the current path
    if (menu.children) {
      for (const child of menu.children) {
        if (child.path && path.startsWith(child.path.split('/').slice(0, -1).join('/') + '/')) {
          return { menu, child }
        }
      }
      // Recursively check children
      const found = findMenuItemByPathPrefix(menu.children, path)
      if (found) return found
    }
  }
  return null
}

const handleCommand = async (command: string) => {
  if (command === 'logout') {
    try {
      await ElMessageBox.confirm(
        '确定要退出登录吗？',
        '退出确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      )
      authStore.logout()
      tabsStore.reset()
      sessionStorage.setItem('logout_message', '已退出登录')
      router.push('/login')
    } catch {
    }
  }
}

// ========== 多标签页：路由变化同步标签 ==========
const tabsStore = useTabsStore()

// 需要排除的路径（不生成标签）
const TAB_EXCLUDED_PATHS = ['/login', '/redirect']

const tabTitleOf = (path: string): string => {
  // 优先用菜单配置的名称（更贴近用户认知），其次路由 meta.title
  const menuItem = findMenuItemByPath(menuConfig, path)
  if (menuItem?.label) return menuItem.label
  const r = router.resolve(path)
  return (r.meta?.title as string) || '未命名'
}

watch(() => route.fullPath, (fullPath) => {
  if (TAB_EXCLUDED_PATHS.some(p => fullPath.startsWith(p))) return
  if (!route.meta?.title) return
  // 带 query 的页面（如详情页）标题取路由 title；标签唯一键=完整路径
  tabsStore.openTab(fullPath, tabTitleOf(route.path))
}, { immediate: true })

// keep-alive 缓存：收集已打开标签对应组件名（页面需在 setup 中 defineOptions({name}) 才生效）
const keepAliveNames = computed<string[]>(() => {
  const names: string[] = []
  for (const t of tabsStore.tabs) {
    try {
      const r = router.resolve(t.path)
      const comp = r.matched[r.matched.length - 1]?.components?.default as { name?: string } | undefined
      if (comp?.name) names.push(comp.name)
    } catch {
      // 忽略无法解析的路径
    }
  }
  return names
})

// ========== 顶栏刷新按钮（对齐 ADP HeaderBar） ==========
const headerRefreshing = ref(false)
const handleHeaderRefresh = () => {
  headerRefreshing.value = true
  // 与 TabBar 右键刷新同一机制：经 /redirect 中转强制组件重建
  router.replace({ path: '/redirect' + route.path }).finally(() => {
    headerRefreshing.value = false
  })
}

// ========== Command Palette ==========
const commandPaletteVisible = ref(false)
const commandKeyword = ref('')
const commandInputRef = ref<HTMLInputElement | null>(null)
const commandLoading = ref(false)
const activeCommandIndex = ref(0)
const currentGroup = ref<'navigation' | 'actions' | 'molds'>('navigation')
const recentCommands = ref<CommandItem[]>([])

// 导航命令列表
const navigationItems: CommandItem[] = [
  { id: 'home', type: 'navigation', title: '首页', path: '/home', icon: 'HomeFilled', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'mold-list', type: 'navigation', title: '模具列表', path: '/mold/list', icon: 'Box', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'mold-create', type: 'navigation', title: '新增模具', path: '/mold/create', icon: 'Plus', iconBg: 'rgba(39, 174, 96, 0.1)' },
  { id: 'slot-check', type: 'navigation', title: '槽宽检测', path: '/mold/slot-check', icon: 'DataLine', iconBg: 'rgba(230, 162, 60, 0.1)' },
  { id: 'task-board', type: 'navigation', title: '加工任务看板', path: '/processing/task-board', icon: 'Grid', iconBg: 'rgba(144, 147, 153, 0.1)' },
  { id: 'task-list', type: 'navigation', title: '加工任务列表', path: '/processing/task-list', icon: 'List', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'mold-initial', type: 'navigation', title: '模具初始列表', path: '/processing/mold-initial-list', icon: 'DocumentCopy', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'storage', type: 'navigation', title: '入库管理', path: '/mold/storage', icon: 'Box', iconBg: 'rgba(39, 174, 96, 0.1)' },
  { id: 'borrow', type: 'navigation', title: '领用申请', path: '/mold/borrow', icon: 'Refresh', iconBg: 'rgba(230, 162, 60, 0.1)' },
  { id: 'borrow-management', type: 'navigation', title: '领还管理', path: '/mold/borrow-management', icon: 'Tools', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'reprocess', type: 'navigation', title: '模具再处理', path: '/mold/reprocess', icon: 'RefreshRight', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'equipment', type: 'navigation', title: '设备列表', path: '/equipment/list', icon: 'Monitor', iconBg: 'rgba(144, 147, 153, 0.1)' },
  { id: 'consumable', type: 'navigation', title: '耗材列表', path: '/consumable/list', icon: 'List', iconBg: 'rgba(39, 174, 96, 0.1)' },
  { id: 'user-mgmt', type: 'navigation', title: '用户管理', path: '/system/user', icon: 'UserIcon', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'role-mgmt', type: 'navigation', title: '角色管理', path: '/system/role', icon: 'SettingIcon', iconBg: 'rgba(35, 131, 226, 0.1)' },
  { id: 'settings', type: 'navigation', title: '系统设置', path: '/system/settings', icon: 'Setting', iconBg: 'rgba(144, 147, 153, 0.1)' },
]

// 快捷操作列表
const actionItems: CommandItem[] = [
  { id: 'logout', type: 'action', title: '退出登录', description: '安全退出当前账户', icon: 'SwitchButton', iconBg: 'rgba(245, 108, 108, 0.1)' },
]

// 计算属性：根据关键词过滤导航命令
const navigationCommands = computed(() => {
  if (!commandKeyword.value) return navigationItems.slice(0, 8)
  
  const keyword = commandKeyword.value.toLowerCase()
  return navigationItems.filter(cmd => 
    cmd.title.toLowerCase().includes(keyword) || 
    cmd.path?.toLowerCase().includes(keyword)
  ).slice(0, 6)
})

// 计算属性：根据关键词过滤操作命令
const actionCommands = computed(() => {
  if (!commandKeyword.value) return []
  
  const keyword = commandKeyword.value.toLowerCase()
  return actionItems.filter(cmd => 
    cmd.title.toLowerCase().includes(keyword) || 
    cmd.description?.toLowerCase().includes(keyword)
  )
})

// 模具搜索结果
const moldSearchResults = ref<Mold[]>([])

// 计算属性：总结果数
const totalResults = computed(() => {
  return navigationCommands.value.length + actionCommands.value.length + moldSearchResults.value.length
})

// 计算属性：命令面板宽度
const commandPaletteWidth = computed(() => {
  const width = window.innerWidth
  if (width < 480) return width - 32
  if (width < 768) return 480
  return 560
})

// 处理命令面板可见性变化
watch(commandPaletteVisible, (visible) => {
  if (visible) {
    nextTick(() => {
      commandInputRef.value?.focus()
      commandKeyword.value = ''
      activeCommandIndex.value = 0
      currentGroup.value = 'navigation'
      loadRecentCommands()
    })
  }
})

// 加载最近使用的命令
const loadRecentCommands = () => {
  try {
    const recent = localStorage.getItem('command_recent')
    if (recent) {
      recentCommands.value = JSON.parse(recent)
    }
  } catch {
    recentCommands.value = []
  }
}

// 保存最近使用的命令
const saveRecentCommand = (cmd: CommandItem) => {
  const index = recentCommands.value.findIndex(c => c.id === cmd.id)
  if (index > -1) {
    recentCommands.value.splice(index, 1)
  }
  recentCommands.value.unshift(cmd)
  if (recentCommands.value.length > 5) {
    recentCommands.value = recentCommands.value.slice(0, 5)
  }
  localStorage.setItem('command_recent', JSON.stringify(recentCommands.value))
}

// 设置当前激活的命令
const setActiveCommand = (index: number, group: 'navigation' | 'actions' | 'molds') => {
  activeCommandIndex.value = index
  currentGroup.value = group
}

// 处理命令输入
const handleCommandInput = () => {
  activeCommandIndex.value = 0
  currentGroup.value = 'navigation'
  
  if (commandKeyword.value.trim()) {
    commandLoading.value = true
    // 防抖搜索
    setTimeout(async () => {
      await searchMolds()
      commandLoading.value = false
    }, 300)
  } else {
    moldSearchResults.value = []
  }
}

// 搜索模具
const searchMolds = async () => {
  if (!commandKeyword.value.trim()) {
    moldSearchResults.value = []
    return
  }
  
  try {
    const result = await getMoldList({
      keyword: commandKeyword.value.trim(),
      pageSize: 10
    })
    moldSearchResults.value = result.records || []
  } catch {
    moldSearchResults.value = []
  }
}

// 处理命令面板键盘事件
const handleCommandKeydown = (e: KeyboardEvent) => {
  const totalGroups = ['navigation', 'actions', 'molds']
  
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    const currentItems = getCurrentGroupItems()
    if (activeCommandIndex.value < currentItems.length - 1) {
      activeCommandIndex.value++
    }
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (activeCommandIndex.value > 0) {
      activeCommandIndex.value--
    }
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const currentItems = getCurrentGroupItems()
    if (currentItems.length > 0 && activeCommandIndex.value < currentItems.length) {
      const item = currentItems[activeCommandIndex.value]
      if (item.type === 'navigation') {
        executeCommand(item)
      } else if (item.type === 'action' && item.id === 'logout') {
        handleCommand('logout')
      }
    }
  } else if (e.key === 'Tab') {
    e.preventDefault()
    const currentIndex = totalGroups.indexOf(currentGroup.value)
    const nextIndex = (currentIndex + 1) % totalGroups.length
    currentGroup.value = totalGroups[nextIndex] as 'navigation' | 'actions' | 'molds'
    activeCommandIndex.value = 0
  } else if (e.key === 'Escape') {
    commandPaletteVisible.value = false
  }
}

// 获取当前分组的项目
const getCurrentGroupItems = () => {
  switch (currentGroup.value) {
    case 'navigation': return navigationCommands.value
    case 'actions': return actionCommands.value
    case 'molds': return moldSearchResults.value.map(m => ({ id: `mold-${m.id}`, type: 'navigation' as const, title: m.moldCode, icon: 'Box', iconBg: 'rgba(35, 131, 226, 0.1)' }))
    default: return []
  }
}

// 执行命令
const executeCommand = (cmd: CommandItem) => {
  saveRecentCommand(cmd)
  
  if (cmd.type === 'navigation' && cmd.path) {
    goToPath(cmd.path)
  } else if (cmd.type === 'action' && cmd.id === 'logout') {
    handleCommand('logout')
  }
}

// 跳转到路径
const goToPath = (path: string) => {
  commandPaletteVisible.value = false
  router.push(path)
}

// 跳转到模具详情
const goToMold = (mold: Mold) => {
  commandPaletteVisible.value = false
  router.push(`/mold/${mold.id}`)
}

// 高亮关键词
const highlightKeyword = (text: string, keyword: string): string => {
  if (!keyword.trim() || !text) return text
  
  const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  return text.replace(regex, '<mark class="highlight">$1</mark>')
}

// 旧的搜索相关代码（保留以兼容其他地方）
const searchVisible = ref(false)
const searchKeyword = ref('')
const searchResults = ref<Mold[]>([])
const searchLoading = ref(false)
const activeSearchIndex = ref(0)
const searchInputRef = ref<HTMLInputElement | null>(null)
let searchTimer: ReturnType<typeof setTimeout> | null = null

const searchHistory = ref<string[]>([])
const SEARCH_HISTORY_KEY = 'mold_search_history'
const MAX_HISTORY_COUNT = 5

const loadSearchHistory = () => {
  try {
    const history = localStorage.getItem(SEARCH_HISTORY_KEY)
    if (history) {
      searchHistory.value = JSON.parse(history)
    }
  } catch {
    searchHistory.value = []
  }
}

const saveSearchHistory = (keyword: string) => {
  const trimmedKeyword = keyword.trim()
  if (!trimmedKeyword) return
  
  const index = searchHistory.value.indexOf(trimmedKeyword)
  if (index > -1) {
    searchHistory.value.splice(index, 1)
  }
  
  searchHistory.value.unshift(trimmedKeyword)
  
  if (searchHistory.value.length > MAX_HISTORY_COUNT) {
    searchHistory.value = searchHistory.value.slice(0, MAX_HISTORY_COUNT)
  }
  
  localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(searchHistory.value))
}

const handleHistoryClick = (keyword: string) => {
  searchKeyword.value = keyword
  handleSearch()
}

const clearSearchHistory = () => {
  searchHistory.value = []
  localStorage.removeItem(SEARCH_HISTORY_KEY)
}

const popoverWidth = computed(() => {
  const width = window.innerWidth
  if (width < 480) {
    return Math.min(width - 32, 360)
  } else if (width < 768) {
    return 400
  } else {
    return 480
  }
})

// 兼容旧的搜索函数（通过命令面板调用）
const handleSearch = () => {
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
  
  if (!searchKeyword.value.trim()) {
    searchResults.value = []
    return
  }
  
  searchLoading.value = true
  
  searchTimer = setTimeout(async () => {
    try {
      const result = await getMoldList({
        keyword: searchKeyword.value.trim(),
        pageSize: 10
      })
      searchResults.value = result.records || []
      activeSearchIndex.value = 0
      
      if (searchResults.value.length > 0) {
        saveSearchHistory(searchKeyword.value)
      }
    } catch {
      searchResults.value = []
    } finally {
      searchLoading.value = false
    }
  }, 300)
}

const handleSearchEnter = () => {
  if (searchResults.value.length > 0 && activeSearchIndex.value >= 0) {
    const result = searchResults.value[activeSearchIndex.value]
    if (result) {
      handleSearchResultClick(result)
    }
  }
}

const handleSearchResultClick = (result: Mold) => {
  searchVisible.value = false
  router.push(`/mold/${result.id}`)
}

const getStatusType = (status?: string): 'success' | 'warning' | 'danger' | 'info' => {
  if (!status) return 'info'
  
  const statusTypes: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
    '在库': 'success',
    '领用': 'warning',
    '调试中': 'warning',
    '成型': 'info',
    '封存': 'info',
    '报废': 'danger',
    '待报废': 'danger'
  }
  
  return statusTypes[status] || 'info'
}

// ========== 全局键盘事件 ==========
const handleGlobalKeydown = (e: KeyboardEvent) => {
  // Ctrl+K 或 Cmd+K 打开命令面板
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    commandPaletteVisible.value = !commandPaletteVisible.value
  }
  
  // 命令面板激活时的键盘导航
  if (commandPaletteVisible.value) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const items = getCurrentGroupItems()
      if (activeCommandIndex.value < items.length - 1) {
        activeCommandIndex.value++
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (activeCommandIndex.value > 0) {
        activeCommandIndex.value--
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const items = getCurrentGroupItems()
      if (items.length > 0 && activeCommandIndex.value < items.length) {
        const item = items[activeCommandIndex.value]
        if ('path' in item && item.path) {
          goToPath(item.path)
        } else if ('id' in item && item.id === 'logout') {
          handleCommand('logout')
        }
      }
    } else if (e.key === 'Escape') {
      commandPaletteVisible.value = false
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
  loadSearchHistory()

  // 检查用户认证状态后再初始化
  const authStore = useAuthStore()
  if (authStore.isAuthenticated && authStore.token) {
    // 用户已认证，初始化 WebSocket 和加载未读通知数量
    notificationStore.initWebSocket()
    refreshNotifications()
  } else {
    // 用户未认证，等待登录完成后加载
    watch(() => authStore.isAuthenticated, (isAuthenticated) => {
      if (isAuthenticated && authStore.token) {
        notificationStore.initWebSocket()
        refreshNotifications()
      }
    }, { once: true })
  }

  const logoutMessage = sessionStorage.getItem('logout_message')
  if (logoutMessage) {
    ElMessage.success(logoutMessage)
    sessionStorage.removeItem('logout_message')
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
  // 断开 WebSocket 连接
  notificationStore.disconnectWebSocket()
})
</script>
  
<style scoped>
.main-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: row;
  background-color: var(--color-bg-secondary);
}

.layout-header {
  position: fixed;
  top: 0;
  left: v-bind('sidebarStore.isCollapsed ? "64px" : "240px"');
  right: 0;
  z-index: 1020;
  height: auto; /* 两行：面包屑行 + 标签行，融为同一块 */
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-bottom: 1px solid var(--color-border-light, #e4e7ed);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow var(--transition-base) var(--transition-easing);
}

.header-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px; /* 紧凑面包屑行 */
  flex-shrink: 0;
  max-width: var(--layout-max-width);
  margin: 0 auto;
  padding: 0 var(--spacing-xl);
  box-sizing: border-box;
}

.header-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

/* ADP 风格顶栏图标按钮 */
.header-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-md, 6px);
  color: var(--color-text-regular, #606266);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.header-icon-btn:hover {
  background-color: var(--color-primary-lighter, #eaf3fc);
  color: var(--color-primary, #2383e2);
}

.header-icon-btn .is-refreshing {
  animation: header-refresh-spin 0.8s linear infinite;
}

@keyframes header-refresh-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.header-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: var(--spacing-md);
}

.search-wrapper {
  position: relative;
}

.search-trigger {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: #f9fafb;
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: all var(--transition-base) var(--transition-easing);
  border: 1px solid #e5e7eb;
}

.search-trigger:hover {
  background-color: #fef3e7;
  border-color: var(--color-primary);
}

.search-trigger:hover .search-text,
.search-trigger:hover .el-icon {
  color: var(--color-primary);
}

.search-text {
  font-size: var(--font-size-sm);
  color: #606266;
}

.search-shortcut {
  font-size: var(--font-size-xs);
  color: #909399;
  background-color: #f3f4f6;
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
  margin-left: var(--spacing-sm);
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  transition: all var(--transition-base) var(--transition-easing);
  background-color: #f9fafb;
}

.user-dropdown:hover {
  background-color: #fef3e7;
  box-shadow: 0 2px 8px rgba(35, 131, 226, 0.15);
}

.user-dropdown:hover .username {
  color: var(--color-primary);
}

.user-dropdown:hover .dropdown-icon {
  color: var(--color-primary);
}

.username {
  font-size: var(--font-size-sm);
  color: #374151;
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-base) var(--transition-easing);
}

.dropdown-icon {
  color: #909399;
  font-size: var(--font-size-xs);
  transition: all var(--transition-base) var(--transition-easing);
}

.layout-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin-left: v-bind('sidebarStore.isCollapsed ? "64px" : "240px"');
  background-color: var(--color-bg-page, #fafbfc);
  max-width: calc(100vw - v-bind('sidebarStore.isCollapsed ? "64px" : "240px"'));
  overflow-x: hidden;
  padding-top: 91px; /* 固定头部整块：面包屑行50 + 标签行40 + 底线1 */
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 内容区对齐 Art Design Pro art-page-content：留白 20px + 白卡片容器 + 大圆角 */
.layout-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  width: calc(100% - 40px);
  margin: 14px auto 16px;
  padding: 20px;
  background-color: var(--color-bg-primary, #ffffff);
  border-radius: var(--border-radius-xl, 12px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  box-sizing: border-box;
}

.layout-main :deep(.el-container) {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.layout-footer {
  flex-shrink: 0;
  height: var(--layout-footer-height);
  background-color: var(--color-bg-primary);
  border-top: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--spacing-xl);
}

.footer-text {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.footer-divider {
  margin: 0 var(--spacing-sm);
  color: var(--color-text-placeholder);
  font-size: var(--font-size-xs);
}

.footer-beian-link {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--transition-base) var(--transition-easing);
}

.footer-beian-link:hover {
  color: var(--color-primary);
  text-decoration: underline;
}

.footer-gongan-link {
  display: inline-flex;
  align-items: center;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--transition-base) var(--transition-easing);
}

.footer-gongan-link:hover {
  color: var(--color-primary);
  text-decoration: underline;
}

.footer-gongan-icon {
  width: 16px;
  height: 16px;
  margin-right: 4px;
  vertical-align: middle;
}

.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all var(--transition-base) var(--transition-easing);
}

.fade-transform-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* 超小屏设备 (手机竖屏) */
@media (max-width: 767px) {
  .layout-main {
    margin-left: 0;
    max-width: 100vw;
  }

  .layout-header {
    left: 0 !important;
  }

  .header-content {
    padding: 0 var(--spacing-sm);
  }

  /* 汉堡菜单按钮 - 仅移动端显示 */
  .mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: var(--border-radius-md);
    cursor: pointer;
    color: #606266;
    flex-shrink: 0;
    transition: all 0.2s ease;
    margin-right: var(--spacing-xs);
  }

  .mobile-menu-btn:active {
    background-color: #fef3e7;
    color: var(--color-primary);
  }

  .logo-text {
    display: none;
  }

  .header-center {
    display: none;
  }

  .username {
    display: none;
  }

  .search-text,
  .search-shortcut {
    display: none;
  }

  .layout-footer {
    height: auto;
    padding: var(--spacing-sm);
  }

  .footer-text {
    font-size: var(--font-size-xs);
  }
}

/* 桌面端隐藏汉堡菜单按钮 */
@media (min-width: 768px) {
  .mobile-menu-btn {
    display: none;
  }
}

/* 小屏设备 (平板) */
@media (min-width: 768px) and (max-width: 1199px) {
  .header-content {
    max-width: 100%;
    padding: 0 var(--spacing-md);
  }

  .logo-text {
    font-size: var(--font-size-md);
  }
}

:deep(.el-breadcrumb__item) {
  font-size: var(--font-size-sm);
  color: #606266;
}

:deep(.el-breadcrumb__item.is-link) {
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}

:deep(.el-breadcrumb__item.is-link:hover) {
  color: var(--color-primary-dark);
}

:deep(.el-breadcrumb__item:last-child) {
  color: #303133;
  font-weight: var(--font-weight-medium);
}

:deep(.el-dropdown-menu) {
  padding: var(--spacing-xs);
}

:deep(.el-dropdown-menu__item) {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  border-radius: var(--border-radius-sm);
}

:deep(.el-dropdown-menu__item:hover) {
  background-color: var(--color-bg-secondary);
}

:deep(.el-dropdown-menu__item .el-icon) {
  margin-right: var(--spacing-sm);
}

.layout-main::-webkit-scrollbar {
  width: 8px;
}

.layout-main::-webkit-scrollbar-track {
  background: var(--color-bg-secondary);
}

.layout-main::-webkit-scrollbar-thumb {
  background: var(--color-border-base);
  border-radius: var(--border-radius-sm);
}

.layout-main::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-secondary);
}
</style>

<style>
.search-popover {
  padding: 0 !important;
  border-radius: var(--border-radius-lg) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.search-panel {
  padding: var(--spacing-md);
}

.search-panel .el-input {
  margin-bottom: var(--spacing-md);
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border-lighter);
  margin-bottom: var(--spacing-sm);
}

.result-count {
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}

.search-results-list {
  max-height: min(400px, 50vh);
  overflow-y: auto;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all var(--transition-base) var(--transition-easing);
}

.search-result-item:hover,
.search-result-item.is-active {
  background-color: var(--color-primary-lighter);
}

.result-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
  color: var(--color-primary);
}

.result-content {
  flex: 1;
  min-width: 0;
}

.result-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: 4px;
}

.mold-code {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.result-info {
  display: flex;
  gap: var(--spacing-md);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.leader {
  color: var(--color-primary);
}

.search-empty,
.search-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  color: var(--color-text-secondary);
  gap: var(--spacing-sm);
}

.search-loading .el-icon {
  font-size: 24px;
  color: var(--color-primary);
}

.search-tips {
  padding: var(--spacing-md);
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
}

.tip-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.tip-item .el-icon {
  color: var(--color-primary);
}

.search-history {
  padding: var(--spacing-md);
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
}

.search-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.clear-history {
  color: var(--color-primary);
  cursor: pointer;
  transition: opacity var(--transition-base);
}

.clear-history:hover {
  opacity: 0.8;
}

.search-history-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.history-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all var(--transition-base) var(--transition-easing);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
}

.history-item:hover {
  background-color: var(--color-primary-lighter);
  color: var(--color-primary);
}

.history-item .el-icon {
  color: var(--color-text-secondary);
  font-size: 14px;
}

.history-item:hover .el-icon {
  color: var(--color-primary);
}

mark.highlight {
  background-color: rgba(35, 131, 226, 0.3);
  color: var(--color-primary);
  padding: 0 2px;
  border-radius: 2px;
  font-weight: var(--font-weight-medium);
}

.notification-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  transition: all var(--transition-base) var(--transition-easing);
  background-color: #f9fafb;
  color: #606266;
}

.notification-trigger:hover {
  background-color: #fef3e7;
  color: var(--color-primary);
}

.notification-trigger .el-badge {
  line-height: 1;
}
</style>

<style>
.notification-popover {
  padding: 0 !important;
  border-radius: var(--border-radius-lg) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.notification-panel {
  max-height: 480px;
  display: flex;
  flex-direction: column;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--color-border-lighter);
}

.notification-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.mark-all-read {
  font-size: var(--font-size-xs);
  color: var(--color-primary);
  cursor: pointer;
  transition: opacity var(--transition-base);
}

.mark-all-read:hover {
  opacity: 0.8;
}

.notification-tabs {
  display: flex;
  padding: var(--spacing-sm) var(--spacing-lg);
  gap: var(--spacing-md);
  border-bottom: 1px solid var(--color-border-lighter);
}

.tab-item {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  transition: all var(--transition-base) var(--transition-easing);
}

.tab-item:hover {
  color: var(--color-primary);
}

.tab-item.active {
  color: var(--color-primary);
  background-color: var(--color-primary-lighter);
  font-weight: var(--font-weight-medium);
}

.notification-list {
  flex: 1;
  overflow-y: auto;
  max-height: 320px;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  cursor: pointer;
  transition: all var(--transition-base) var(--transition-easing);
  position: relative;
}

.notification-item:hover {
  background-color: var(--color-bg-secondary);
}

.notification-item.unread {
  background-color: rgba(35, 131, 226, 0.05);
}

.notification-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.notification-desc {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-time {
  font-size: var(--font-size-xs);
  color: var(--color-text-placeholder);
}

.notification-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-primary);
  position: absolute;
  right: var(--spacing-lg);
  top: 50%;
  transform: translateY(-50%);
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  color: var(--color-text-secondary);
  gap: var(--spacing-sm);
}

.notification-footer {
  padding: var(--spacing-md) var(--spacing-lg);
  text-align: center;
  border-top: 1px solid var(--color-border-lighter);
}

.notification-footer span {
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  cursor: pointer;
  transition: opacity var(--transition-base);
}

.notification-footer span:hover {
  opacity: 0.8;
}
</style>

<style>
/* ========== Command Palette Styles ========== */
.command-palette-popover {
  padding: 0 !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid var(--color-border-lighter);
  overflow: hidden;
}

.command-palette {
  display: flex;
  flex-direction: column;
  max-height: min(480px, 60vh);
  overflow: hidden;
}

/* 命令输入框 */
.command-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--color-border-lighter);
  background-color: var(--color-bg-primary);
}

.command-input-wrapper .input-icon {
  color: var(--color-text-placeholder);
  font-size: 18px;
  flex-shrink: 0;
}

.command-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background: transparent;
  color: var(--color-text-primary);
}

.command-input::placeholder {
  color: var(--color-text-placeholder);
}

.escape-hint {
  font-size: 11px;
  padding: 2px 6px;
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: 4px;
  color: var(--color-text-secondary);
  font-family: inherit;
}

/* 命令结果区域 */
.command-results {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-sm);
}

.command-group {
  margin-bottom: var(--spacing-sm);
}

.command-group:last-child {
  margin-bottom: 0;
}

.command-group-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: 11px;
  color: var(--color-text-placeholder);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.command-group-header .el-icon {
  font-size: 12px;
}

.group-count {
  margin-left: auto;
  padding: 0 6px;
  background-color: var(--color-bg-secondary);
  border-radius: 8px;
  font-size: 10px;
}

.command-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.command-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.15s ease;
}

.command-item:hover,
.command-item.is-active {
  background-color: var(--color-primary-lighter);
}

.command-item .command-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-md);
  flex-shrink: 0;
}

.command-item .command-icon .el-icon {
  font-size: 16px;
}

.command-item .command-icon.mold-icon {
  background-color: rgba(35, 131, 226, 0.1);
  color: #2383e2;
}

.command-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.command-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.command-desc {
  font-size: 12px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.command-desc .leader {
  color: var(--color-primary);
}

.command-shortcut {
  flex-shrink: 0;
  font-size: 10px;
  padding: 2px 6px;
  background-color: var(--color-bg-secondary);
  border: none;
}

/* 空状态 */
.command-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  color: var(--color-text-secondary);
  gap: var(--spacing-sm);
}

.command-empty .el-icon {
  font-size: 32px;
  color: var(--color-text-placeholder);
}

.empty-tips {
  font-size: 12px;
  color: var(--color-text-placeholder);
  text-align: center;
}

/* 加载状态 */
.command-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg);
  color: var(--color-primary);
}

.command-loading .el-icon {
  font-size: 20px;
}

/* 默认状态（无搜索关键词） */
.command-default {
  padding: var(--spacing-md);
}

.keyboard-hints {
  display: flex;
  justify-content: center;
  gap: var(--spacing-xl);
  padding: var(--spacing-sm) 0;
  margin-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--color-border-lighter);
}

.hint-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 12px;
  color: var(--color-text-secondary);
}

.hint-item kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  font-size: 11px;
  font-family: inherit;
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: 4px;
  color: var(--color-text-primary);
}

.quick-commands {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--spacing-sm);
}

.quick-command-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-command-item:hover {
  background-color: var(--color-primary-lighter);
  transform: translateY(-2px);
}

.quick-command-item .el-icon {
  font-size: 20px;
  color: var(--color-primary);
}

.quick-command-item span {
  font-size: 12px;
  color: var(--color-text-secondary);
  text-align: center;
}

.quick-command-item:hover span {
  color: var(--color-primary);
}

/* 高亮样式 */
mark.highlight {
  background-color: rgba(35, 131, 226, 0.3);
  color: var(--color-primary);
  padding: 0 2px;
  border-radius: 2px;
  font-weight: 500;
}

/* 滚动条 */
.command-results::-webkit-scrollbar {
  width: 6px;
}

.command-results::-webkit-scrollbar-track {
  background: transparent;
}

.command-results::-webkit-scrollbar-thumb {
  background: var(--color-border-base);
  border-radius: 2px;
}

.command-results::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-secondary);
}

/* 响应式 */
@media (max-width: 767px) {
  .command-palette-popover {
    width: calc(100vw - 32px) !important;
    left: 16px !important;
  }
  
  .quick-commands {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .keyboard-hints {
    display: none;
  }
}
</style>
